<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Policy from 'dashboard/components/policy.vue';

import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import ScenariosCard from 'dashboard/components-next/assistant/shared/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/assistant/shared/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/assistant/shared/AddNewScenariosDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
// Без agentId страница работает как общий каталог сценариев аккаунта (п.7):
// та же вёрстка, но CRUD идёт по верхнеуровневому API.
const agentId = computed(() =>
  route.params.agentId ? Number(route.params.agentId) : null
);

const uiFlags = useMapGetter('assistantScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const scenarios = useMapGetter('assistantScenarios/getRecords');

const searchQuery = ref('');

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('ASSISTANT.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('ASSISTANT.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('ASSISTANT.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

const updateScenario = async scenario => {
  try {
    await store.dispatch('assistantScenarios/update', {
      id: scenario.id,
      agentId: agentId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('ASSISTANT.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ASSISTANT.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('assistantScenarios/delete', {
      id,
      agentId: agentId.value,
    });
    useAlert(t('ASSISTANT.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ASSISTANT.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  await Promise.all(
    idsArray.map(id =>
      store.dispatch('assistantScenarios/delete', {
        id,
        agentId: agentId.value,
      })
    )
  );
  bulkSelectedIds.value = new Set();
  useAlert(t('ASSISTANT.SCENARIOS.API.DELETE.SUCCESS'));
};

const addScenario = async scenario => {
  try {
    await store.dispatch('assistantScenarios/create', {
      agentId: agentId.value,
      title: scenario.title,
      description: scenario.description,
      instruction: scenario.instruction,
      enabled: true,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('ASSISTANT.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ASSISTANT.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

watch(
  agentId,
  newId => {
    store.dispatch('assistantScenarios/get', {
      agentId: newId,
    });
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('assistantAgents/get');
});
</script>

<template>
  <PageLayout
    v-model:search-query="searchQuery"
    :header-title="$t('ASSISTANT.SCENARIOS.TITLE')"
    :description="$t('ASSISTANT.SCENARIOS.DESCRIPTION')"
    :search-placeholder="t('ASSISTANT.SCENARIOS.LIST.SEARCH_PLACEHOLDER')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-agent-switcher="!!agentId"
  >
    <template #headerActions>
      <Policy :permissions="['administrator']">
        <AddNewScenariosDialog @add="addScenario" />
      </Policy>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <BulkSelectBar
          v-if="bulkSelectedIds.size > 0"
          v-model="bulkSelectedIds"
          :all-items="scenarios"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          :delete-label="
            $t('ASSISTANT.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
          "
          @bulk-delete="bulkDeleteScenarios"
        />
        <div v-if="scenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('ASSISTANT.SCENARIOS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else-if="filteredScenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('ASSISTANT.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-2">
          <ScenariosCard
            v-for="scenario in filteredScenarios"
            :id="scenario.id"
            :key="scenario.id"
            :title="scenario.title"
            :description="scenario.description"
            :instruction="scenario.instruction"
            :tools="scenario.tools"
            :is-selected="bulkSelectedIds.has(scenario.id)"
            :selectable="
              hoveredCard === scenario.id || bulkSelectedIds.size > 0
            "
            @select="handleRuleSelect"
            @delete="deleteScenario(scenario.id)"
            @update="updateScenario"
            @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
